<template>
  <div class="cflc">
    <!-- Linked fields — draggable cards -->
    <div v-if="items.length > 0" class="cf-list">
      <draggable
        :list="items"
        @end="onDragEnd"
        handle=".field-card__drag-handle"
        :animation="180"
        ghost-class="field-card--ghost"
        chosen-class="field-card--chosen"
      >
        <div v-for="(item, idx) in items" :key="'CFLC' + item.id" class="field-card">
          <div class="field-card__body">
            <i class="tim-icons el-icon-rank field-card__drag-handle" title="Drag to reorder"></i>
            <div class="field-card__type-icon">
              <i :class="typeIcon(item)"></i>
            </div>
            <div class="field-card__info">
              <div class="field-card__label text-white">{{ item.name || item.label }}</div>
              <div class="field-card__meta">
                <span class="field-card__type-name">{{ typeName(item) }}</span>
              </div>
            </div>
            <div class="field-card__actions">
              <i v-if="item._saving" class="el-icon-loading field-card__saving" title="Saving"></i>
              <el-tooltip :content="removeTooltip" effect="light" :open-delay="300" placement="top">
                <base-button
                  class="field-card__remove-btn"
                  simple
                  type="danger"
                  link
                  size="sm"
                  @click="$emit('remove', idx)"
                >
                  <i class="tim-icons icon-simple-remove"></i>
                </base-button>
              </el-tooltip>
            </div>
          </div>
          <div v-if="togglesFor(item).length > 0" class="field-card__controls">
            <el-tooltip
              v-for="t in togglesFor(item)"
              :key="t.key"
              :content="t.tooltip || t.label"
              effect="light"
              :open-delay="300"
              placement="top"
            >
              <label class="field-card__toggle mb-0">
                <el-switch
                  active-color="#e14eca"
                  :value="!!item[t.key]"
                  @change="(val) => $emit('toggle', idx, t.key, val ? 1 : 0)"
                />
                <span class="field-card__toggle-label">{{ t.label }}</span>
              </label>
            </el-tooltip>
          </div>
        </div>
      </draggable>
    </div>

    <!-- Library has fields but none linked — dashed panel ABOVE the add
         row so the section doesn't read as a stray dropdown + orphan
         sentence. -->
    <div v-if="!libraryEmpty && items.length === 0" class="cflc__empty">
      <i class="tim-icons icon-bullet-list-67 cflc__empty-icon"></i>
      <p class="cflc__empty-title text-white">{{ emptyTitle }}</p>
      <p class="cflc__empty-hint">{{ emptyLinkedHint }}</p>
    </div>

    <!-- Add from library -->
    <div v-if="addable.length > 0" class="cf-add-row" :class="{ 'mt-3': items.length > 0 }">
      <el-select
        :value="null"
        class="add-custom-field-select"
        placeholder="+ Add custom field"
        filterable
        :popper-append-to-body="true"
        popper-class="cflc__picker-popper"
        size="small"
        @change="$emit('add', $event)"
      >
        <el-option
          v-for="field in addable"
          :key="'CFLCA' + field.id"
          :label="field.name || field.label"
          :value="field.id"
        >
          <span class="cflc__option-label">{{ field.name || field.label }}</span>
          <span class="add-custom-field-select__option-type">{{ typeName(field) }}</span>
        </el-option>
      </el-select>
    </div>

    <!-- No fields exist at all → point at the library. -->
    <div v-if="libraryEmpty" class="text-center py-3">
      <img
        src="/img/pandas/empty-custom-fields.png"
        alt="No custom fields"
        style="width: 120px; height: 120px; object-fit: contain;"
      />
      <p class="cflc__empty-copy">
        No custom fields have been created. Go to
        <a href="/settings/custom-fields" class="text-primary">Settings &gt; Custom fields</a>
        to create one.
      </p>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

/**
 * Shared "link custom fields from the library" card list — extracted from
 * BookingSiteSettings' ContentTab so the payment-page editor (and any
 * future surface) renders custom-field linking identically: draggable
 * field cards + filterable add-select + the two empty states.
 *
 * Purely presentational; the parent owns the data and persistence model
 * (staged-save on payment pages, immediate-save on booking site).
 *
 * Items/addable accept either {name} or {label}, and `type` as the INT
 * code (1 checkbox / 2 input / 3 dropdown) or the booking-site {id} object.
 *
 * Events:
 *  - add(fieldId)              — picked from the library dropdown
 *  - remove(index)             — card's ✕
 *  - reorder(items)            — after drag; positions already renumbered
 *  - toggle(index, key, 0|1)   — only when `toggles` config is provided
 */
export default {
  name: 'custom-field-link-cards',
  components: { draggable },
  props: {
    items: { type: Array, required: true },
    addable: { type: Array, default: () => [] },
    /** [{ key, label, tooltip, appliesTo?: (item) => bool }] */
    toggles: { type: Array, default: () => [] },
    removeTooltip: { type: String, default: 'Remove' },
    emptyTitle: { type: String, default: 'No custom fields linked' },
    emptyLinkedHint: {
      type: String,
      default: 'Add fields like PO number or contact email to capture extras at checkout.',
    },
  },
  computed: {
    libraryEmpty() {
      return this.items.length === 0 && this.addable.length === 0;
    },
  },
  methods: {
    typeId(f) {
      const t = f && f.type;
      return typeof t === 'object' && t !== null ? Number(t.id) : Number(t);
    },
    typeName(f) {
      const map = { 1: 'CHECKBOX', 2: 'INPUT FIELD', 3: 'DROPDOWN' };
      return map[this.typeId(f)] || 'FIELD';
    },
    typeIcon(f) {
      const map = {
        1: 'far fa-check-square',
        2: 'tim-icons icon-pencil',
        3: 'tim-icons icon-bullet-list-67',
      };
      return map[this.typeId(f)] || 'tim-icons icon-bullet-list-67';
    },
    togglesFor(item) {
      return this.toggles.filter((t) => !t.appliesTo || t.appliesTo(item));
    },
    onDragEnd() {
      this.items.forEach((f, i) => { f.position = i; });
      this.$emit('reorder', this.items);
    },
  },
};
</script>

<style scoped>
/* Lifted verbatim from BookingSiteSettings ContentTab so both surfaces
   stay pixel-identical. If you tweak here, migrate ContentTab onto this
   component rather than forking the styles again. */
.field-card + .field-card {
  margin-top: 6px;
}
.field-card {
  container-type: inline-size;
  border-radius: 0.375rem;
  padding: 10px 12px;
  background: rgba(128, 128, 128, 0.05);
  transition: background 0.15s ease;
}
.field-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.field-card--chosen {
  background: rgba(225, 78, 202, 0.08);
}
.field-card--ghost {
  opacity: 0.35;
  background: rgba(225, 78, 202, 0.05);
}
.field-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: nowrap;
  min-width: 0;
}
.field-card__drag-handle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.35);
  cursor: grab;
  flex-shrink: 0;
  padding: 4px;
  margin: -4px;
  transition: color 0.15s ease;
  touch-action: none;
}
.field-card__drag-handle:active {
  cursor: grabbing;
}
.field-card:hover .field-card__drag-handle {
  color: rgba(255, 255, 255, 0.7);
}
.field-card__type-icon {
  font-size: 1.1em;
  color: #e14eca;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}
.field-card__info {
  flex: 1 1 0;
  min-width: 90px;
}
.field-card__label {
  font-size: clamp(0.7rem, 2.4cqw, 0.9rem);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.field-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
}
.field-card__type-name {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.55);
}
.field-card__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
/* Toggle row below the header — mirrors InternalBookingFields' controls
   row (small switches, 11px labels) now that three toggles can show. */
.field-card__controls {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-left: 70px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.field-card__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.field-card__toggle-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}
.field-card__toggle ::v-deep .el-switch__core {
  height: 16px;
  width: 32px !important;
  border-radius: 8px;
}
.field-card__toggle ::v-deep .el-switch__core::after {
  width: 12px;
  height: 12px;
}
.field-card__toggle ::v-deep .el-switch.is-checked .el-switch__core::after {
  margin-left: -13px;
}
.field-card__remove-btn {
  min-width: 32px;
  min-height: 32px;
  padding: 6px;
  margin-left: 4px;
}
.cf-add-row {
  margin-top: 10px;
}
.add-custom-field-select {
  width: 240px;
  max-width: 100%;
}
.add-custom-field-select ::v-deep .el-input__inner {
  height: 36px;
  line-height: 36px;
  border: 1px dashed rgba(225, 78, 202, 0.4);
  background: rgba(225, 78, 202, 0.03);
  color: rgba(225, 78, 202, 0.9);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding-left: 14px;
  transition: background 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}
.add-custom-field-select ::v-deep .el-input__inner:hover {
  border-color: rgba(225, 78, 202, 0.7);
  background: rgba(225, 78, 202, 0.08);
}
.add-custom-field-select ::v-deep .el-input--suffix.is-focus .el-input__inner,
.add-custom-field-select ::v-deep .el-input__inner:focus {
  border-style: solid;
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  color: #e14eca;
}
.add-custom-field-select ::v-deep .el-input__inner::placeholder {
  color: rgba(225, 78, 202, 0.9);
  font-weight: 600;
}
.add-custom-field-select ::v-deep .el-input__icon {
  color: rgba(225, 78, 202, 0.7);
  line-height: 36px;
}
.cflc__empty-copy {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  max-width: 340px;
  margin: 0.5rem auto;
}
.cflc__empty {
  border: 1px dashed rgba(125, 125, 125, 0.35);
  border-radius: 8px;
  padding: 28px 16px;
  text-align: center;
}
.cflc__empty-icon {
  font-size: 26px;
  color: rgba(125, 125, 125, 0.6);
}
.cflc__empty-title {
  font-size: 15px;
  font-weight: 600;
  margin: 10px 0 2px;
}
.cflc__empty-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  max-width: 380px;
  margin: 0 auto;
}
@container (max-width: 480px) {
  .field-card { padding: 8px 10px; }
  .field-card__body { gap: 10px; }
  .field-card__drag-handle { font-size: 12px; }
  .field-card__controls { gap: 14px; padding-left: 38px; }
  .field-card__remove-btn { min-width: 28px; min-height: 28px; padding: 4px; margin-left: 0; }
  .add-custom-field-select { width: 100%; max-width: none; }
}
</style>

<style>
/* Unscoped: the select's dropdown popper appends to <body>, and the
   light-mode overrides target ancestors outside this component. */
/* Cap the popper and wrap long labels — same fix as the booking
   drawer's internal-fields picker; without it one long field label
   stretches the dropdown across the viewport. */
.cflc__picker-popper {
  max-width: 480px;
}
.cflc__picker-popper .el-select-dropdown__item {
  height: auto;
  min-height: 34px;
  line-height: 1.4;
  padding: 8px 20px;
  white-space: normal;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.cflc__picker-popper .cflc__option-label {
  flex: 1;
  white-space: normal;
  word-break: break-word;
}
.cflc__picker-popper .add-custom-field-select__option-type {
  flex-shrink: 0;
  float: none;
  padding-top: 2px;
  margin-left: 0;
}
.add-custom-field-select__option-type {
  float: right;
  font-size: 10px;
  text-transform: uppercase;
  color: rgba(125, 125, 125, 0.8);
  letter-spacing: 0.04em;
  margin-left: 12px;
}
.white-content .field-card {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .field-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .field-card__label.text-white {
  color: rgba(0, 0, 0, 0.85) !important;
}
.white-content .field-card__drag-handle { color: rgba(0, 0, 0, 0.3); }
.white-content .field-card:hover .field-card__drag-handle { color: rgba(0, 0, 0, 0.6); }
.white-content .field-card__type-icon { color: #cc5fad; }
.white-content .field-card__meta,
.white-content .field-card__type-name { color: rgba(0, 0, 0, 0.6); }
.white-content .field-card__toggle-label { color: rgba(0, 0, 0, 0.7); }
.white-content .add-custom-field-select .el-input__inner {
  background: rgba(225, 78, 202, 0.02);
}
.white-content .add-custom-field-select .el-input__inner::placeholder {
  color: rgba(204, 95, 173, 0.95);
}
.white-content .cflc__empty-copy { color: rgba(0, 0, 0, 0.6); }
.white-content .cflc__empty { border-color: rgba(0, 0, 0, 0.15); }
.white-content .cflc__empty-title.text-white { color: rgba(0, 0, 0, 0.85) !important; }
.white-content .cflc__empty-hint { color: rgba(0, 0, 0, 0.55); }
.white-content .cflc__empty-icon { color: rgba(0, 0, 0, 0.35); }
</style>
